/* tslint:disable:max-file-line-count */

import { action, computed, intercept, observable } from 'mobx';

import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';
import { SerializedData, Store } from 'bernie-core';

import { PropertySort } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from './events/store-events';

import { SearchCriteriaUtils } from './models/search-criteria-utils';

/**
 * @deprecated Use SearchCriteriaModel
 */
export class SearchCriteriaStore extends Store {
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4; // 4AM
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

  public static getDefaults() {
    return {
      adults: '2',
      children: '',
      destination: '',
      endDate: null,
      group: '',
      latLong: '',
      regionId: '',
      selected: '',
      startDate: null,
      userIntent: '',
      neighborhood: '',
      sort: PropertySort.RECOMMENDED,
    };
  }

  public localDateFormat: string;

  constructor(state: SerializedData = {}, l10n: ILocalization, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.l10n = l10n || new Localization('en_US');
    this.localDateFormat = state.localDateFormat || 'yyyy-MM-dd';

    this.dateInterceptor('startDate');
    this.dateInterceptor('endDate');

    Object.assign(this, SearchCriteriaStore.getDefaults(), SearchCriteriaUtils.getFilterDefaults(), state);
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public dateInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeDate(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
        // accept null to clear out dates
        else if (change.newValue === null) {
          return change;
        }
      });
    }
  }

  // keep dates in the data format that we want, in this case ISO, and the UI can localize based on this date
  public sanitizeDate(date) {
    if (date) {
      try {
        let dateObj: Date = this.l10n.parseLocalDateString(date, { raw: this.localDateFormat }) as Date;

        if (!dateObj) {
          dateObj = this.l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
        }

        const cleanDate = this.l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

        if (cleanDate) {
          return cleanDate;
        }

        throw new Error(`Invalid Date: cannot parse ${date} into proper date`);
      } catch (error) {
        if (this.logger) {
          this.logger.logEvent(STORE_EVENTS.MOBX_INTERCEPTOR_ERROR, getErrorString(error));
        }

        return '';
      }
    }
  }

  // return a whitelisted or blacklisted version of the store
  public toJSON(toBeRemoved = []) {
    return super.toJSON(['batchRequest', 'changedFilters', 'l10n'].concat(toBeRemoved));
  }

  @observable
  public l10n: ILocalization;

  // Search Form
  @observable
  public startDate = null;
  @observable
  public endDate = null;
  @observable
  public destination = '';
  @observable
  public regionId = '';
  @observable
  public adults = '2';
  @observable
  public children = '';
  @observable
  public selected = '';
  @observable
  public experience = '';

  // Filter Form
  @observable
  public sort = PropertySort.RECOMMENDED;
  @observable
  public hotelName = '';
  @observable
  public price = [];
  @observable
  public star = [];
  @observable
  public lodging = [];
  @observable
  public amenities = [];
  @observable
  public travelerType = [];
  @observable
  public guestRating = '';
  @observable
  public changedFilters: any = {};
  @observable
  public accessibility = [];
  @observable
  public paymentType = [];
  @observable
  public cleaningAndSafetyPractices = [];
  @observable
  public bedroomFilter = [];
  @observable
  public deals = [];
  @observable
  public propertyStyle = [];
  @observable
  public rewards = [];
  @observable
  public commissionTier = [];
  @observable
  public agencyBusinessModel = [];
  @observable
  public mealPlan = [];
  @observable
  public poi = '';
  @observable
  public neighborhood = '';

  // Code refactoring need to be done in order to implement batch request functionality on UI.
  // Adding a field here in order to make minimal changes and to make sure that the existing functionality dont break
  // when the backend code gets merged.
  @observable
  public batchRequest: any = {};

  // Optional fields
  @observable
  public latLong = '';

  @computed
  get guestCount(): number {
    return this.adultsCount + this.childrenCount;
  }

  @computed
  get adultsCount(): number {
    let adults;
    if (typeof this.adults === 'number') {
      adults = this.adults;
    } else if (typeof this.adults === 'string' && this.adults !== '') {
      adults = parseInt(
        this.adults.split(',').reduce((acc, cur) => String(parseInt(acc, 10) + parseInt(cur, 10))),
        10
      );
    } else {
      adults = 0;
    }

    return adults;
  }

  @computed
  get childrenCount(): number {
    let children;
    if (typeof this.children === 'number') {
      children = this.children;
    } else if (typeof this.children === 'string' && this.children !== '') {
      children = this.children.split(',').length;
    } else {
      children = 0;
    }

    return children;
  }

  @computed
  get roomCount(): number {
    return this.adults.split(',').length;
  }

  @action
  public setValues(query: any = {}): void {
    if (!query.adults) {
      query.adults = '2';
    }

    if (!query.children || query.children === '0' || query.children === 0) {
      query.children = '';
    }

    query = SearchCriteriaUtils.ensureCorrectTypes(query);

    Object.assign(this, query);
  }
}
